import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import type Hls from 'hls.js';
import { askQuestion, fetchRoom, heartbeat, recordCtaClick, type Project, type RoomState, type RoomView } from '../api';
import { formatClock, formatCountdown, formatSession, minuteOf } from '../format';
import { Header } from './register';

interface Props {
  project: Project;
  token: string;
}

const HEARTBEAT_MS = 10_000;

/**
 * Der Raum rechnet zwischen zwei Serverantworten selbst weiter: Er merkt sich den Zustand samt
 * Zeitpunkt und zählt lokal hoch. Alle 10 Sekunden gleicht die Anschau-Meldung ihn wieder ab.
 */
function useLiveState(view: RoomView | null): RoomState | null {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(timer);
  }, []);
  void tick;
  if (!view) return null;
  const elapsed = (Date.now() - new Date(view.serverTime).getTime()) / 1000;
  const state = view.state;
  if (state.kind === 'waiting') {
    const left = state.secondsUntilStart - elapsed;
    return left > 0 ? { kind: 'waiting', secondsUntilStart: left } : { kind: 'playing', positionSeconds: -left };
  }
  if (state.kind === 'playing') {
    const position = state.positionSeconds + elapsed;
    return position < view.webinar.durationSeconds ? { kind: 'playing', positionSeconds: position } : { kind: 'ended' };
  }
  return state;
}

export function RoomPage({ project, token }: Props) {
  const [view, setView] = useState<RoomView | null | undefined>(undefined);
  const [entered, setEntered] = useState(false);
  const [takenOver, setTakenOver] = useState(false);
  const device = useRef({ id: newDeviceId(), openedAt: new Date().toISOString() });

  const reload = useCallback(() => {
    fetchRoom(token)
      .then((v) => {
        setView(v);
        document.title = `${v.webinar.title} – ${project.name}`;
      })
      .catch(() => setView(null));
  }, [token, project.name]);

  useEffect(reload, [reload]);

  const live = useLiveState(view ?? null);

  // Übergänge (Start, Ende) holen den Zustand frisch vom Server – die Wahrheit liegt dort.
  const liveKind = live?.kind;
  useEffect(() => {
    if (view && liveKind && liveKind !== view.state.kind) reload();
  }, [liveKind, view, reload]);

  // Der Moment, in dem das Angebot erscheinen soll: nicht auf die nächste Anschau-Meldung warten.
  const ctaDue = !!view && !view.cta && view.ctaOffsetSeconds !== null && live?.kind === 'playing' && live.positionSeconds >= view.ctaOffsetSeconds;
  useEffect(() => {
    if (ctaDue) reload();
  }, [ctaDue, reload]);

  // Anschau-Meldung alle 10 Sekunden, sobald der Besucher den Raum betreten hat.
  useEffect(() => {
    if (!entered || takenOver || liveKind !== 'playing') return;
    const send = () =>
      heartbeat(token, device.current.id, device.current.openedAt)
        .then((r) => {
          if (r.takenOver) setTakenOver(true);
          else setView((v) => (v ? { ...v, state: r.state, cta: r.cta ?? v.cta, serverTime: r.serverTime } : v));
        })
        .catch(() => undefined);
    send();
    const timer = setInterval(send, HEARTBEAT_MS);
    return () => clearInterval(timer);
  }, [entered, takenOver, liveKind, token]);

  if (view === undefined) return null;
  if (view === null) {
    return (
      <>
        <Header project={project} />
        <main className="page page--message">
          <h1>Diesen Zugang gibt es nicht</h1>
          <p className="muted">Bitte prüfe den Link aus deiner Anmeldung.</p>
        </main>
      </>
    );
  }
  if (!live) return null;

  if (view.cancelled) {
    return (
      <>
        <Header project={project} />
        <main className="page page--message">
          <h1>Du hast für diesen Termin abgesagt</h1>
          <p className="muted">Kein Problem. Wenn du doch dabei sein willst, such dir einfach einen neuen Termin aus – deine Daten sind noch da.</p>
          <p>
            <a className="button" href={`/umbuchen/${token}`}>
              Neuen Termin wählen
            </a>
          </p>
        </main>
      </>
    );
  }

  if (takenOver) {
    return (
      <>
        <Header project={project} />
        <main className="page page--message">
          <h1>Du hast das Webinar auf einem anderen Gerät geöffnet</h1>
          <p className="muted">Dort läuft es weiter. Dein Zugang gilt für ein Gerät zur selben Zeit.</p>
          <p>
            <button type="button" className="button button--secondary" onClick={() => window.location.reload()}>
              Hier weiterschauen
            </button>
          </p>
        </main>
      </>
    );
  }

  const minute = live.kind === 'playing' ? minuteOf(live.positionSeconds) : null;
  const totalMinutes = Math.round(view.webinar.durationSeconds / 60);
  const status =
    live.kind === 'playing' ? (
      <>
        <span className="pill">
          <span className="pill__dot" />
          Vorführung läuft
        </span>
        <span className="muted small">
          Minute {minute} von {totalMinutes}
        </span>
      </>
    ) : live.kind === 'waiting' ? (
      <span className="pill">Beginnt in {formatCountdown(live.secondsUntilStart)}</span>
    ) : live.kind === 'ended' ? (
      <span className="pill">Vorführung beendet</span>
    ) : null;

  return (
    <>
      <Header project={project} right={status} />
      <div className="notice-bar">Dies ist eine Aufzeichnung, die zu festen Terminen gezeigt wird.</div>

      {live.kind === 'too_late' ? (
        <main className="page page--message">
          <h1>Diese Vorführung läuft schon zu lange</h1>
          <p className="muted">Ein Einstieg lohnt sich jetzt nicht mehr. Such dir einen der nächsten Termine aus – deine Daten bleiben gespeichert.</p>
          <p>
            <a className="button" href={`/w/${view.webinar.slug}`}>
              Nächste Termine ansehen
            </a>
          </p>
        </main>
      ) : (
        <main className="room">
          <section className="room__main">
            <Player view={view} live={live} entered={entered} onEnter={() => setEntered(true)} token={token} />
            <h1 style={{ fontSize: 24 }}>{view.webinar.title}</h1>
            {live.kind === 'waiting' && (
              <p className="muted">
                Dein Termin: {formatSession(view.session.startsAt, project.timezone)}. Du kannst dieses Fenster geöffnet lassen – es geht von selbst los.
              </p>
            )}
            {live.kind === 'ended' && <p className="muted">Danke fürs Dabeisein{view.registration.name ? `, ${view.registration.name}` : ''}.</p>}
            {view.cta && <Offer view={view} token={token} />}
          </section>

          <aside className="room__side">
            <Hints view={view} live={live} />
            {(live.kind === 'playing' || live.kind === 'ended') && <QuestionForm token={token} />}
          </aside>
        </main>
      )}
    </>
  );
}

function Player({ view, live, entered, onEnter, token }: { view: RoomView; live: RoomState; entered: boolean; onEnter: () => void; token: string }) {
  const progress = live.kind === 'playing' ? (live.positionSeconds / view.webinar.durationSeconds) * 100 : live.kind === 'ended' ? 100 : 0;
  const showVideo = view.video !== null && live.kind === 'playing' && entered;
  // Hintergrund, solange kein Video läuft: im Wartezimmer das eigene Bild, sonst das Vorschaubild des Videos.
  const backdrop = live.kind === 'waiting' ? (view.waitingImageUrl ?? view.video?.posterUrl ?? null) : (view.video?.posterUrl ?? null);
  const frameRef = useRef<HTMLDivElement>(null);
  const [fullscreen, setFullscreen] = useState(false);

  // Vollbild: wo der Browser es für den Rahmen kann (Rechner, iPad), echtes Vollbild – unsere Knöpfe bleiben.
  // Auf dem iPhone gibt es das nicht; dort füllt der Rahmen per CSS den Bildschirm. Apples eigenes
  // Video-Vollbild bleibt außen vor: Es bringt Pause- und Spulknöpfe mit und pausiert beim Schließen.
  const toggleFullscreen = () => {
    const frame = frameRef.current;
    if (!frame) return;
    if (document.fullscreenEnabled && frame.requestFullscreen) {
      if (document.fullscreenElement) void document.exitFullscreen().catch(() => undefined);
      else void frame.requestFullscreen().catch(() => setFullscreen(true));
      return;
    }
    setFullscreen((f) => !f);
  };
  useEffect(() => {
    const sync = () => setFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', sync);
    return () => document.removeEventListener('fullscreenchange', sync);
  }, []);
  useEffect(() => {
    if (!fullscreen || document.fullscreenElement) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setFullscreen(false);
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [fullscreen]);

  return (
    <div ref={frameRef} className={`player${fullscreen && !document.fullscreenElement ? ' player--fullscreen' : ''}`}>
      {showVideo ? (
        <HlsVideo hlsUrl={view.video!.hlsUrl} posterUrl={view.video!.posterUrl} positionSeconds={live.positionSeconds} fullscreen={fullscreen} fullscreenEnabled={view.webinar.fullscreenEnabled} onToggleFullscreen={toggleFullscreen} />
      ) : (
        <div
          className={`player__placeholder${backdrop && live.kind !== 'playing' ? ' player__placeholder--overlay' : ''}`}
          style={backdrop && live.kind !== 'playing' ? { backgroundImage: `url(${backdrop})` } : undefined}
        >
          <div className="player__badge">
            {live.kind === 'playing' && entered && (
              <>
                <span className="player__clock">{formatClock(live.positionSeconds)}</span>
                <span>Videobild folgt mit der Videoverarbeitung</span>
              </>
            )}
            {live.kind === 'waiting' && (
              <>
                <span className="player__clock">{formatCountdown(live.secondsUntilStart)}</span>
                <span>bis zum Start</span>
              </>
            )}
            {live.kind === 'ended' && <span>Die Vorführung ist beendet</span>}
          </div>
        </div>
      )}
      {/* iOS spielt Videos mit Ton nicht von selbst ab: der Klick zählt als Nutzerinteraktion (Konzept 8.3). */}
      {live.kind === 'playing' && !entered && (
        <div className="enter">
          <p className="muted" style={{ color: '#d0d0d0' }}>
            Die Vorführung läuft bereits seit {minuteOf(live.positionSeconds)} Minuten – du steigst mitten ein.
          </p>
          <button type="button" className="button button--large" onClick={onEnter}>
            Webinar betreten
          </button>
        </div>
      )}
      {/* Im Vollbild bleibt das Angebot sichtbar: als Banner über dem Video, wie bei Webinaris (Konzept 3.8). */}
      {fullscreen && view.cta && (
        <div className="player__offer">
          <div className="player__offer-text">
            <strong>{view.cta.headline}</strong>
            {view.cta.text && <span className="small">{view.cta.text}</span>}
          </div>
          <a className="button button--small" href={view.cta.url} target="_blank" rel="noopener" onClick={() => void recordCtaClick(token).catch(() => undefined)}>
            {view.cta.buttonLabel}
          </a>
        </div>
      )}
      <div className="player__progress">
        <div style={{ width: `${Math.min(100, progress)}%` }} />
      </div>
    </div>
  );
}

/**
 * Der Videoplayer: HLS über hls.js (Safari kann es von Haus aus). Keine Bedienleiste – die
 * Position bestimmt der Server. Weicht das Video mehr als drei Sekunden davon ab (Ruckler,
 * Spulversuch), wird es zurückgesetzt. Ton und Vollbild bleiben bedienbar.
 */
function HlsVideo({ hlsUrl, posterUrl, positionSeconds, fullscreen, fullscreenEnabled, onToggleFullscreen }: { hlsUrl: string; posterUrl: string; positionSeconds: number; fullscreen: boolean; fullscreenEnabled: boolean; onToggleFullscreen: () => void }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const hlsRef = useRef<Hls | null>(null);
  const wanted = useRef(positionSeconds);
  const [muted, setMuted] = useState(false);
  wanted.current = positionSeconds;

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    let hls: Hls | null = null;
    let cancelled = false;
    const start = () => {
      video.currentTime = wanted.current;
      void video.play().catch(() => undefined);
    };
    if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = hlsUrl;
      video.addEventListener('loadedmetadata', start, { once: true });
    } else {
      // hls.js wird erst geladen, wenn wirklich ein Video läuft – die Anmeldeseite bleibt leicht.
      void import('hls.js').then(({ default: HlsLib }) => {
        if (cancelled || !HlsLib.isSupported()) return;
        hls = new HlsLib({ startPosition: wanted.current, capLevelToPlayerSize: true, maxBufferLength: 30, maxMaxBufferLength: 90, backBufferLength: 30 });
        hlsRef.current = hls;
        hls.loadSource(hlsUrl);
        hls.attachMedia(video);
        hls.on(HlsLib.Events.MANIFEST_PARSED, () => void video.play().catch(() => undefined));
        // Netzfehler: weiterladen. Medienfehler: Decoder neu aufsetzen. Alles andere: neu beginnen.
        hls.on(HlsLib.Events.ERROR, (_event, data) => {
          if (!data.fatal) return;
          if (data.type === HlsLib.ErrorTypes.NETWORK_ERROR) hls?.startLoad();
          else if (data.type === HlsLib.ErrorTypes.MEDIA_ERROR) hls?.recoverMediaError();
          else {
            hls?.destroy();
            hlsRef.current = null;
          }
        });
      });
    }
    return () => {
      cancelled = true;
      hls?.destroy();
      hlsRef.current = null;
    };
  }, [hlsUrl]);

  // Gleichlauf: Das Video folgt der Serverzeit, nicht umgekehrt. Gesprungen wird erst ab acht
  // Sekunden Abweichung und nie, während es gerade puffert – häufige Sprünge oder Tempowechsel
  // bringen Decoder aus dem Tritt (Ton läuft weiter, Bild bleibt stehen).
  useEffect(() => {
    const video = videoRef.current;
    if (!video || video.readyState < 3 || video.seeking || video.paused) return;
    if (Math.abs(positionSeconds - video.currentTime) > 8) video.currentTime = positionSeconds;
  }, [positionSeconds]);

  // Wächter gegen eingefrorene Bilder. Zwei Fälle:
  //  1. Zeit steht, Ton steht → Puffer leer oder Decoder hängt: kleiner Schubs, dann Selbstheilung.
  //  2. Zeit läuft, Ton läuft, aber es kommen keine Bilder mehr → der Videodecoder hängt: Selbstheilung
  //     (hls.js setzt die Decoder neu auf), sonst ein Sprung auf die Stelle, an der wir sind.
  useEffect(() => {
    let lastTime = -1;
    let lastFrames = -1;
    let stuckTime = 0;
    let stuckFrames = 0;
    const timer = setInterval(() => {
      const video = videoRef.current;
      if (!video || video.paused || video.ended || video.seeking || video.readyState < 2) return;
      const frames = video.getVideoPlaybackQuality ? video.getVideoPlaybackQuality().totalVideoFrames : -1;

      if (video.currentTime === lastTime) {
        stuckTime += 1;
        if (stuckTime === 1) video.currentTime = video.currentTime + 0.25;
        else if (stuckTime >= 2) {
          hlsRef.current?.recoverMediaError();
          stuckTime = 0;
        }
      } else {
        stuckTime = 0;
      }

      if (frames >= 0 && frames === lastFrames && video.currentTime > lastTime + 2) {
        stuckFrames += 1;
        if (hlsRef.current) hlsRef.current.recoverMediaError();
        else video.currentTime = video.currentTime;
        if (stuckFrames >= 3) window.location.reload();
      } else if (frames !== lastFrames) {
        stuckFrames = 0;
      }

      lastTime = video.currentTime;
      lastFrames = frames;
    }, 4000);
    return () => clearInterval(timer);
  }, []);

  return (
    <>
      <video
        ref={videoRef}
        poster={posterUrl}
        playsInline
        muted={muted}
        controls={false}
        controlsList="nodownload noplaybackrate"
        disablePictureInPicture
        onSeeking={(e) => {
          const v = e.currentTarget;
          if (Math.abs(v.currentTime - wanted.current) > 6) v.currentTime = wanted.current;
        }}
        // Eine Vorführung wartet auf niemanden: Wird das Video von außen angehalten, läuft es weiter,
        // solange die Seite sichtbar ist – an der Stelle, die der Server vorgibt.
        onPause={(e) => {
          const v = e.currentTarget;
          if (v.ended || document.visibilityState !== 'visible') return;
          if (Math.abs(v.currentTime - wanted.current) > 6) v.currentTime = wanted.current;
          void v.play().catch(() => undefined);
        }}
        onContextMenu={(e) => e.preventDefault()}
      />
      <div className="player__controls">
        <button type="button" className="player__control" onClick={() => setMuted((m) => !m)} aria-label={muted ? 'Ton einschalten' : 'Ton ausschalten'}>
          {muted ? 'Ton an' : 'Ton aus'}
        </button>
        {/* Vollbild je Webinar schaltbar; das Angebot erscheint darin als Banner (Player). Auf dem iPhone gibt es kein
            echtes Vollbild – dort füllt der Rahmen per CSS den Bildschirm, Apples Video-Vollbild bleibt außen vor. */}
        {fullscreenEnabled && (
          <button type="button" className="player__control" onClick={onToggleFullscreen} aria-label={fullscreen ? 'Vollbild beenden' : 'Vollbild'}>
            {fullscreen ? 'Vollbild beenden' : 'Vollbild'}
          </button>
        )}
      </div>
    </>
  );
}

function Offer({ view, token }: { view: RoomView; token: string }) {
  const cta = view.cta!;
  return (
    <div className="offer">
      <div className="offer__body">
        <span className="label label--brand">Zum Webinar passend</span>
        <h2 className="offer__title">{cta.headline}</h2>
        {cta.text && <p className="muted">{cta.text}</p>}
      </div>
      <div className="offer__action">
        {/* Neues Tab: Das Webinar läuft hier weiter (Konzept 3.8). */}
        <a className="button" href={cta.url} target="_blank" rel="noopener" onClick={() => void recordCtaClick(token).catch(() => undefined)}>
          {cta.buttonLabel}
        </a>
        <span className="faint small">Öffnet ein neues Tab, das Webinar läuft weiter.</span>
      </div>
    </div>
  );
}

function Hints({ view, live }: { view: RoomView; live: RoomState }) {
  const position = live.kind === 'playing' ? live.positionSeconds : live.kind === 'ended' ? Number.POSITIVE_INFINITY : -1;
  const visible = view.hints.filter((h) => h.offsetSeconds <= position);
  if (visible.length === 0) return null;
  return (
    <div className="card">
      <span className="label">Hinweise vom Team</span>
      <div className="hints">
        {visible.map((h) => (
          <div key={h.offsetSeconds} className="hint">
            <span className="hint__time">{formatClock(h.offsetSeconds)}</span>
            <span>{h.text}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function QuestionForm({ token }: { token: string }) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState<number | null>(null);
  const [error, setError] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (busy || !text.trim()) return;
    setBusy(true);
    setError(false);
    try {
      const r = await askQuestion(token, text);
      setSent(r.positionSeconds);
      setText('');
    } catch {
      setError(true);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="card" onSubmit={submit}>
      <div className="field">
        <label htmlFor="frage">Deine Frage</label>
        <textarea id="frage" rows={3} placeholder="Geht nur an uns, nicht an die anderen Teilnehmer." value={text} onChange={(e) => setText(e.target.value)} maxLength={2000} />
      </div>
      {sent !== null && <p className="muted small">Danke – deine Frage ist angekommen (bei Minute {minuteOf(sent)}). Du bekommst die Antwort per E-Mail.</p>}
      {error && <div className="error">Das hat nicht geklappt. Bitte versuch es noch einmal.</div>}
      <button type="submit" className="button button--secondary" disabled={busy || !text.trim()}>
        Frage absenden
      </button>
    </form>
  );
}

function newDeviceId(): string {
  const bytes = new Uint8Array(12);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}
